import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { forgetPassword , reset } from '../features/forget-password';
import {
    selectForgetPasswordLoading,
    selectForgetPasswordSuccess,
    selectForgetPasswordError,
    selectForgetPasswordMessage
} from '../features/forget-password/forget-password.selectors';

interface UseForgetPasswordOptions {
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

interface FormData {
    email: string;
    newPassword: string;
    confirmPassword: string;
}

interface FormErrors {
    email?: string;
    newPassword?: string;
    confirmPassword?: string;
}

export const useForgetPassword = (options: UseForgetPasswordOptions = {}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const isLoading = useSelector(selectForgetPasswordLoading);
    const isSuccess = useSelector(selectForgetPasswordSuccess);
    const isError = useSelector(selectForgetPasswordError);
    const message = useSelector(selectForgetPasswordMessage);

    const [formData, setFormData] = useState<FormData>({
        email: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string>('');

    const validateEmail = useCallback((email: string): string | undefined => {
        if (!email) {
            return t('REGISTRATION_EMAIL_REQUIRED');
        }
        if (!email.includes('@')) {
            return t('REGISTRATION_EMAIL_INVALID');
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return t('REGISTRATION_EMAIL_INVALID');
        }
        return;
    }, [t]);

    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};
        const emailError = validateEmail(formData.email);

        if (emailError) {
            newErrors.email = emailError;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData.email, validateEmail]);

    const handleInputChange = useCallback((value: string) => {
        setFormData(prev => ({ ...prev, email: value }));

        if (errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }));
        }
        if (submitError) {
            setSubmitError('');
        }
    }, [errors.email, submitError]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        dispatch(forgetPassword(formData.email) as any);
    }, [formData.email, validateForm, dispatch]);


    useEffect(() => {
        if (isSuccess) {
            toast.success(t('FORGET_PASSWORD_SUCCESS') as string);
            
            if (options.onSuccess) {
                options.onSuccess();
            }
            dispatch(reset());;
        }
    }, [isSuccess, t, options, reset()]);

    useEffect(() => {
        if (isError && message) {
            setSubmitError(message);
            
            const translated = t(message);
            toast.error(translated !== message ? translated : message);
            if (options.onError) {
                options.onError(message);
            }
            dispatch(reset());
        }
    }, [isError, message, t, options , reset()]);

    return {
        formData,
        errors,
        submitError,
        isSubmitting: isLoading,
        isSuccess,
        isError,
        handleInputChange,
        handleSubmit,
        reset: () => dispatch(reset()),
        validateEmail,
        validateForm,
    };
};

export default useForgetPassword;
